import React, { useState, useEffect } from 'react';
import { getControlEvents } from '../api';
import { useTranslation } from '../i18n/LanguageContext';
import {
  ShieldCheck, CheckCircle, Warning, Info, Clock,
  CaretRight, Funnel
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  open: { bg: 'bg-alert-critical/10', text: 'text-alert-critical', dot: 'bg-alert-critical' },
  in_progress: { bg: 'bg-alert-warning/10', text: 'text-alert-warning', dot: 'bg-alert-warning' },
  closed: { bg: 'bg-success/10', text: 'text-success', dot: 'bg-success' },
};

const SEVERITY_ICONS = {
  critical: { icon: Warning, color: 'text-alert-critical' },
  warning: { icon: Warning, color: 'text-alert-warning' },
  info: { icon: Info, color: 'text-info' },
};

export default function GovernanceDashboard({ industry, summary, loading }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        const status = statusFilter === 'all' ? undefined : statusFilter;
        const { data } = await getControlEvents(industry, status);
        setEvents(data.events || []);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, [industry, statusFilter]);

  return (
    <div className="space-y-6" data-testid="governance-dashboard">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
          {t('governance.title')}
        </h1>
        <p className="font-body text-sm text-text-secondary mt-1">
          {t('governance.subtitle')}
        </p>
      </div>

      {/* Governance KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface border border-border rounded-lg p-4 animate-fade-in stagger-1" data-testid="gov-kpi-assurance">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={18} weight="duotone" className="text-tertiary" />
            <span className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Assurance</span>
          </div>
          <p className="font-heading text-2xl font-bold text-text-primary">
            {loading ? '—' : `${summary?.kpis?.governance?.assurance_coverage || 0}%`}
          </p>
          <p className="text-xs font-body text-text-secondary mt-1">KPI coverage with evidence</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4 animate-fade-in stagger-2" data-testid="gov-kpi-open">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} weight="duotone" className="text-alert-critical" />
            <span className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Open Issues</span>
          </div>
          <p className="font-heading text-2xl font-bold text-alert-critical">
            {events.filter(e => e.status === 'open').length}
          </p>
          <p className="text-xs font-body text-text-secondary mt-1">Requiring action</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4 animate-fade-in stagger-3" data-testid="gov-kpi-progress">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} weight="duotone" className="text-alert-warning" />
            <span className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">In Progress</span>
          </div>
          <p className="font-heading text-2xl font-bold text-alert-warning">
            {events.filter(e => e.status === 'in_progress').length}
          </p>
          <p className="text-xs font-body text-text-secondary mt-1">Under resolution</p>
        </div>

        <div className="bg-surface border border-border rounded-lg p-4 animate-fade-in stagger-4" data-testid="gov-kpi-closed">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} weight="duotone" className="text-success" />
            <span className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary">Resolved</span>
          </div>
          <p className="font-heading text-2xl font-bold text-success">
            {events.filter(e => e.status === 'closed').length}
          </p>
          <p className="text-xs font-body text-text-secondary mt-1">Completed controls</p>
        </div>
      </div>

      {/* Control Events Timeline */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-semibold text-text-primary">Control Events Timeline</h3>
          <div className="flex items-center gap-2">
            <Funnel size={14} className="text-tertiary" />
            <select
              data-testid="event-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-1 text-xs font-body bg-background border border-border rounded-md text-text-primary focus:outline-none focus:border-primary"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {loadingEvents ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-background rounded-lg animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="text-center text-sm font-body text-text-secondary py-8">No control events found</p>
        ) : (
          <div className="space-y-2">
            {events.map((event, i) => {
              const sevConfig = SEVERITY_ICONS[event.severity] || SEVERITY_ICONS.info;
              const SevIcon = sevConfig.icon;
              const statusColor = STATUS_COLORS[event.status] || STATUS_COLORS.open;
              return (
                <div
                  key={i}
                  data-testid={`control-event-${i}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-tertiary/40 transition-all duration-200 group"
                >
                  <div className="mt-0.5">
                    <SevIcon size={18} weight="duotone" className={sevConfig.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-body font-semibold uppercase tracking-wider ${statusColor.bg} ${statusColor.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} />
                        {event.status.replace('_', ' ')}
                      </span>
                      <span className="text-[10px] font-body text-tertiary uppercase tracking-wider">
                        {event.type.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="font-body text-sm text-text-primary mt-1">{event.message}</p>
                    <p className="font-body text-xs text-text-secondary mt-0.5">{event.site} — {event.industry}</p>
                  </div>
                  <CaretRight size={14} className="text-border group-hover:text-tertiary transition-colors mt-2" />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
